// Shared utility helpers for owner-portal pages.
import React from "react";

export const STAGE_TONE = {
  Selection: { bg: "rgba(91,97,150,0.10)", fg: "#5B6196" },
  Consultation: { bg: "rgba(177,78,47,0.10)", fg: "#8F3C23" },
  Purchase: { bg: "rgba(185,138,60,0.12)", fg: "#8a651f" },
  "Expedited Review": { bg: "rgba(58,85,99,0.10)", fg: "#3A5563" },
  Build: { bg: "rgba(79,97,73,0.10)", fg: "#4F6149" },
  "Construction Inspection": { bg: "rgba(79,97,73,0.10)", fg: "#4F6149" },
  Complete: { bg: "rgba(27,31,75,0.10)", fg: "#1B1F4B" },
};

export const CONSULT_TONE = {
  "Consultation Requested": { bg: "#B14E2F", fg: "#FBFAFD" },
  "In Consultation": { bg: "#1B1F4B", fg: "#FBFAFD" },
  Activated: { bg: "#4F6149", fg: "#FBFAFD" },
  Declined: { bg: "#9AA0C0", fg: "#FBFAFD" },
};

export function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function relativeDate(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 14) return `${days} days ago`;
    if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function StagePill({ stage, testId }) {
  const t = STAGE_TONE[stage] || STAGE_TONE.Selection;
  return (
    <span
      data-testid={testId}
      style={{
        padding: "3px 9px",
        borderRadius: 999,
        backgroundColor: t.bg,
        color: t.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {stage}
    </span>
  );
}

export function ConsultPill({ status, testId }) {
  const t = CONSULT_TONE[status] || CONSULT_TONE["In Consultation"];
  const label =
    status === "Consultation Requested"
      ? "Requested"
      : status === "In Consultation"
        ? "In Consultation"
        : status;
  return (
    <span
      data-testid={testId}
      style={{
        padding: "3px 9px",
        borderRadius: 999,
        backgroundColor: t.bg,
        color: t.fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

export function fullPrice(n) {
  return n == null ? "—" : `$${Number(n).toLocaleString("en-US")}`;
}

/* ──────────────────────────────────────────────────────────────
   "Waiting on …" hero pill — used end-to-end (Workspace cards,
   /owner/projects rows, Project Detail header) so the owner always
   sees the same shape and colour for "who's blocking me right
   now". One source of truth, three places.
   ────────────────────────────────────────────────────────────── */

export function stageBlocker(stage) {
  const s = (stage || "").toLowerCase();
  if (s === "selection")
    return { who: "You", color: "#8F3C23", tone: "owner" };
  if (s === "consultation")
    return { who: "Architect", color: "#1B1F4B", tone: "architect" };
  if (s === "purchase")
    return { who: "You", color: "#8F3C23", tone: "owner" };
  if (s === "expedited review" || s === "review")
    return { who: "City Staff", color: "#3A5563", tone: "city" };
  if (s === "build")
    return { who: "Architect", color: "#1B1F4B", tone: "architect" };
  if (s === "construction inspection")
    return { who: "City Staff", color: "#3A5563", tone: "city" };
  if (s === "move-in")
    return { who: "You", color: "#8F3C23", tone: "owner" };
  if (s === "complete")
    return { who: "—", color: "#6C6B85", tone: "done" };
  return { who: "—", color: "#6C6B85", tone: "done" };
}

const BLOCKER_BG = {
  owner: "#FFF6EA",
  architect: "#EEEEF7",
  city: "#EBF2F4",
  done: "#F3F2F8",
};

export function WaitingOnPill({ stage, size = "md", testId }) {
  const blocker = stageBlocker(stage);
  const bg = BLOCKER_BG[blocker.tone] || "#F3F2F8";
  const small = size === "sm";
  const isDone = blocker.tone === "done";
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: small ? 6 : 8,
        background: bg,
        color: blocker.color,
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 800,
        fontSize: small ? 10.5 : 11.5,
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        padding: small ? "4px 8px" : "5px 10px",
        borderRadius: 999,
        lineHeight: 1,
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden
        style={{
          width: small ? 5 : 6,
          height: small ? 5 : 6,
          borderRadius: 999,
          background: blocker.color,
        }}
      />
      {isDone ? "Completed" : `Waiting on ${blocker.who}`}
    </span>
  );
}
